import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatTL } from "@/lib/api";

export default function Cart() {
  const { items, remove, updateQty, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [priced, setPriced] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (items.length === 0) { setPriced({ items: [], total: 0 }); return; }
    setLoading(true);
    api.post("/cart/price", items).then(r => setPriced(r.data)).finally(()=>setLoading(false));
  }, [items, user]);

  if (items.length === 0) {
    return (
      <div className="kz-container py-20 text-center">
        <ShoppingBag size={48} className="mx-auto text-ink-300 mb-4"/>
        <h1 className="font-display text-3xl font-black mb-2">Sepetiniz Boş</h1>
        <p className="text-ink-500 mb-6">Kataloğumuzdan ürün ekleyerek alışverişe başlayın.</p>
        <Link to="/katalog" className="kz-btn-primary">Kataloğa Git <ArrowRight size={18}/></Link>
      </div>
    );
  }

  return (
    <div className="kz-container py-10">
      <h1 className="font-display text-3xl md:text-4xl font-black mb-8">Sepetim ({items.length})</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        <div className="kz-card divide-y divide-ink-200">
          {priced.items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 p-4">
              <Link to={`/urun`} className="w-20 h-20 bg-ink-100 shrink-0 overflow-hidden">
                <img src={it.image_url} alt="" className="w-full h-full object-cover"/>
              </Link>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate">{it.name}</div>
                <div className="text-xs font-mono text-ink-500">SKU: {it.sku}</div>
                <div className="text-sm font-semibold mt-1">{formatTL(it.unit_price)}
                  {it.special_price && <span className="ml-2 text-[10px] bg-b2b text-white px-1.5 py-0.5 font-bold">ÖZEL FİYAT</span>}
                  {it.campaign_percent > 0 && <span className="ml-2 text-[10px] bg-brand text-white px-1.5 py-0.5 font-bold">KAMPANYA -%{it.campaign_percent}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center border border-ink-200">
                  <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5 hover:bg-ink-100"><Minus size={14}/></button>
                  <span className="px-3 font-mono text-sm">{it.quantity}</span>
                  <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5 hover:bg-ink-100"><Plus size={14}/></button>
                </div>
                <div className="font-bold text-sm">{formatTL(it.unit_price * it.quantity)}</div>
                <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-ink-500 hover:text-brand text-xs flex items-center gap-1">
                  <Trash2 size={14}/> Kaldır
                </button>
              </div>
            </div>
          ))}
        </div>

        <aside className="kz-card p-6 h-fit lg:sticky lg:top-24">
          <div className="font-display font-black text-lg mb-4 pb-4 border-b border-ink-200">SİPARİŞ ÖZETİ</div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-ink-500">Ara Toplam</span>
            <span className="font-semibold">{formatTL(priced.total)}</span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-ink-500">Kargo</span>
            <span className="font-semibold text-brand">Ücretsiz</span>
          </div>
          {user?.role === "b2b_dealer" && user?.b2b_status === "approved" && (
            <div className="text-xs text-b2b bg-b2b/10 px-3 py-2 mb-3 border border-b2b/30">
              💼 Bayi indiriminiz fiyatlara yansıtıldı (%{user.b2b_discount}).
            </div>
          )}
          <div className="flex justify-between text-lg font-display font-black border-t border-ink-200 pt-3 mt-3">
            <span>Toplam</span>
            <span>{formatTL(priced.total)}</span>
          </div>
          {!user && (
            <div data-testid="cart-login-required" className="text-xs bg-brand/10 border border-brand/30 text-ink-900 px-3 py-2 mt-3">
              Sipariş verebilmek için üye girişi yapmanız gerekiyor.
            </div>
          )}
          <button
            data-testid="checkout-btn"
            disabled={loading}
            onClick={() => navigate(user ? "/odeme" : "/giris?next=/odeme")}
            className="kz-btn-primary w-full mt-6"
          >
            {user ? "Siparişi Tamamla" : "Giriş Yap ve Devam Et"} <ArrowRight size={18}/>
          </button>
          {!user && <Link data-testid="cart-register-link" to="/kayit?next=/odeme" className="block text-center text-sm font-semibold text-brand hover:underline mt-3">Hesabınız yok mu? Hemen üye olun</Link>}
          <button onClick={clear} className="w-full text-xs text-ink-500 hover:text-brand mt-3">Sepeti Temizle</button>
        </aside>
      </div>
    </div>
  );
}
